/**
 * DashboardPage.js - 后台数据仪表盘
 * 展示总订单数、总收入、客户数、今日订单等关键指标，以及访客统计和最近订单列表
 * 支持日期筛选，可查看历史日期的数据概览
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Typography,
  Spin,
  message,
  Space,
  Progress,
  List as AntList,
  Badge,
  DatePicker,
} from 'antd';
import {
  RiseOutlined,
  MoneyCollectOutlined,
  TeamOutlined,
  GlobalOutlined,
  BarChartOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminDashboardApi } from '../../api/index';

const { Title, Text } = Typography;

// 订单状态对应颜色映射
const statusColorMap = {
  pending: 'orange',
  paid: 'green',
  shipped: 'blue',
  completed: 'purple',
  cancelled: 'red',
};

// 订单状态中文标签
const statusLabelMap = {
  pending: '待支付',
  paid: '已支付',
  shipped: '已发货',
  completed: '已完成',
  cancelled: '已取消',
};

// 最近订单表格列配置
const columns = [
  {
    title: '订单号',
    dataIndex: 'orderNo',
    key: 'orderNo',
    render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>,
  },
  {
    title: '客户邮箱',
    dataIndex: 'customerEmail',
    key: 'customerEmail',
    ellipsis: true,
  },
  {
    title: '状态',
    dataIndex: 'status',
    key: 'status',
    render: (status) => (
      <Tag color={statusColorMap[status] || 'default'}>
        {statusLabelMap[status] || status}
      </Tag>
    ),
  },
  {
    title: '金额',
    dataIndex: 'total',
    key: 'total',
    render: (val) => `$${Number(val || 0).toFixed(2)}`,
    align: 'right',
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (val) =>
      val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
  },
];

/** 简易柱状图组件（纯 CSS，无第三方依赖） */
function MiniBarChart({ data, height = 120 }) {
  if (!data || data.length === 0) {
    return <Text type="secondary" style={{ fontSize: 12 }}>暂无数据</Text>;
  }
  const maxVal = Math.max(...data.map((d) => Number(d.visitors) || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, paddingTop: 8 }}>
      {data.map((d, i) => {
        const val = Number(d.visitors) || 0;
        const barH = Math.max((val / maxVal) * (height - 40), 2);
        return (
          <div key={i} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#1677ff', marginBottom: 4 }}>
              {val}
            </div>
            <div
              style={{
                height: barH,
                background: 'linear-gradient(180deg, #1677ff 0%, #69b1ff 100%)',
                borderRadius: '4px 4px 0 0',
                transition: 'height 0.3s',
                marginBottom: 4,
              }}
              title={`${d.date}: ${val} 访客`}
            />
            <div style={{ fontSize: 10, color: '#999', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {d.date}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const fetchStats = useCallback(async (date) => {
    setLoading(true);
    try {
      const dateStr = date ? date.format('YYYY-MM-DD') : null;
      const data = await adminDashboardApi.getStats(dateStr);
      setStats(data);
    } catch (err) {
      message.error('加载仪表盘数据失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(selectedDate);
    // 每 30 秒刷新一次在线访客数据（仅当天）
    const timer = setInterval(() => {
      if (selectedDate && selectedDate.isSame(dayjs(), 'day')) {
        fetchStats(selectedDate);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [fetchStats, selectedDate]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchStats(date);
  };

  // 判断是否为今天
  const isToday = selectedDate && selectedDate.isSame(dayjs(), 'day');

  const visitor = stats?.visitorStats;

  // 顶部统计卡片数据
  const dateLabel = isToday ? '今日' : selectedDate ? selectedDate.format('MM-DD') : '今日';
  const statCards = [
     {
      title: `${dateLabel}订单`,
      value: stats?.todayOrders ?? 0,
      icon: <RiseOutlined style={{ fontSize: 28, color: '#722ed1' }} />,
      color: '#f9f0ff',
      precision: 0,
    },
    {
      title: `${dateLabel}收入`,
      value: stats?.todayRevenue ?? 0,
      icon: <MoneyCollectOutlined style={{ fontSize: 28, color: '#eb2f96' }} />,
      color: '#fff0f6',
      prefix: '$',
      precision: 2,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          数据概览
        </Title>
        <DatePicker
          value={selectedDate}
          onChange={handleDateChange}
          format="YYYY-MM-DD"
          placeholder="选择日期"
          style={{ width: 160 }}
          suffixIcon={<CalendarOutlined />}
          allowClear={false}
          disabledDate={(current) => current && current > dayjs().endOf('day')}
        />
      </div>

      <Spin spinning={loading}>
        {/* 统计卡片区域 */}
        <Row gutter={[16, 16]}>
          {statCards.map((card) => (
            <Col xs={24} sm={12} lg={6} key={card.title}>
              <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Statistic
                    title={<span style={{ fontSize: 13, color: '#666' }}>{card.title}</span>}
                    value={card.value}
                    prefix={card.prefix}
                    precision={card.precision}
                    valueStyle={{ fontSize: 26, fontWeight: 600 }}
                  />
                  <div
                    style={{
                      width: 52, height: 52, borderRadius: 12, background: card.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}
                  >
                    {card.icon}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* 访客统计区域 */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          {/* 实时访客指标 */}
          <Col xs={24} lg={6}>
            <Card title={<span><TeamOutlined /> {dateLabel}访客概况</span>} style={{ borderRadius: 8, height: '100%' }} bodyStyle={{ padding: '16px 20px' }}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {isToday && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">当前在线</Text>
                    <Badge status="success" text={
                      <span style={{ fontSize: 22, fontWeight: 600, color: '#52c41a' }}>
                        {visitor?.onlineVisitors ?? 0}
                      </span>
                    } />
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary">{dateLabel}访客</Text>
                  <span style={{ fontSize: 18, fontWeight: 600 }}>{visitor?.todayVisitors ?? 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="secondary">{dateLabel}浏览量</Text>
                  <span style={{ fontSize: 18, fontWeight: 600 }}>{visitor?.todayPageViews ?? 0}</span>
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text type="secondary">转化率</Text>
                    <span style={{ fontWeight: 600 }}>{visitor?.conversionRate ?? 0}%</span>
                  </div>
                  <Progress
                    percent={visitor?.conversionRate ?? 0}
                    showInfo={false}
                    strokeColor="#722ed1"
                    size="small"
                  />
                </div>
              </Space>
            </Card>
          </Col>

          {/* 7 天访客趋势 */}
          <Col xs={24} lg={10}>
            <Card title={<span><BarChartOutlined /> 近 7 天访客趋势</span>} style={{ borderRadius: 8, height: '100%' }} bodyStyle={{ padding: '16px 20px' }}>
              <MiniBarChart data={visitor?.dailyVisitors || []} height={180} />
            </Card>
          </Col>

          {/* 地区分布 */}
          <Col xs={24} lg={8}>
            <Card title={<span><GlobalOutlined /> {dateLabel}访客地区</span>} style={{ borderRadius: 8, height: '100%' }} bodyStyle={{ padding: '8px 0' }}>
              {visitor?.countryStats && visitor.countryStats.length > 0 ? (
                <AntList
                  size="small"
                  dataSource={visitor.countryStats}
                  renderItem={(item, index) => {
                    const maxVisitors = Math.max(...visitor.countryStats.map((c) => Number(c.visitors) || 0), 1);
                    const pct = Math.round(((Number(item.visitors) || 0) / maxVisitors) * 100);
                    return (
                      <AntList.Item style={{ padding: '8px 20px', border: 'none' }}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 12 }}>
                          <span style={{
                            width: 20, height: 20, borderRadius: 10, background: '#f0f0f0',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, color: '#999', flexShrink: 0,
                          }}>
                            {index + 1}
                          </span>
                          <span style={{ flex: 1, fontSize: 13 }}>{item.country}</span>
                          <Progress percent={pct} showInfo={false} size="small" style={{ width: 80, margin: 0 }} />
                          <span style={{ fontWeight: 600, fontSize: 13, minWidth: 32, textAlign: 'right' }}>
                            {item.visitors}
                          </span>
                        </div>
                      </AntList.Item>
                    );
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>暂无数据</div>
              )}
            </Card>
          </Col>
        </Row>

        {/* 最近订单列表 */}
        <Card
          title="最近订单"
          style={{ marginTop: 16, borderRadius: 8 }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            dataSource={stats?.recentOrders || []}
            columns={columns}
            rowKey={(row) => row.id || row.orderNo}
            pagination={false}
            locale={{ emptyText: '暂无订单数据' }}
            scroll={{ x: 600 }}
          />
        </Card>
      </Spin>
    </div>
  );
}
